/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import ReferenceResolver from './ReferenceResolver';
import MetricRoleType from './MetricRoleType';
import SentimentType from './SentimentType';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (static) // getMoserObjectByIdForExpression
//@import com/ibm/bi/platform/moser/common/generated/metadata/MetricRoleType (static) // fromValue
//@import com/ibm/bi/platform/moser/common/generated/metadata/SentimentType (static) // fromValue

/**
 * This class is used to represent a tree structure suggesting related query items to include in a metric definition. A item can represent a query subject or query item. A item representing a query subject is called a qsNode. A item representing a query item is called a qiNode. A qsNode can have qiNodes or qsNodes as children nodes. A qiNode cannot have children nodes. A qsNode with another qsNode as a parent will have a relationshipRef to show the relationship between the two query subject nodes. A qsNode cannot be marked checked or unchecked A qiNode can be marked as checked or unchecked. A checked node indicates the related query item is a candidate for the metric definition.
 * @author  alan-tran
 */
export default class MetricDefinitionHierarchyItem extends BaseMoserObject{

	ref = null;

	relatedRef = null;

	relationshipRef = null;

	metricRoleType = null;

	metricSentiment = null;

	children = null;



	constructor(ref) {
		super();
		this.setRef(ref);
		this.setRelatedRef(null);
		this.setRelationshipRef(null);
		this.setChildren(null);
	}

	/**
	 * @return {ObjectType}  the ref
	 */
	getRef() {
		return this.ref;
	}

	/**
	 * @param  ref  the ref to set
	 */
	setRef(obj) {
		this.ref = obj;
	}

	/**
	 * @return {ObjectType}  the relatedRef
	 */
	getRelatedRef() {
		return this.relatedRef;
	}

	/**
	 * @param  relatedRef  the relatedRef to set
	 */
	setRelatedRef(obj) {
		this.relatedRef = obj;
	}

	/**
	 * @return {Relationship}  the relationshipRef
	 */
	getRelationshipRef() {
		return this.relationshipRef;
	}

	/**
	 * @param  relationshipRef  the relationshipRef to set
	 */
	setRelationshipRef(relationship) {
		this.relationshipRef = relationship;
	}

	/**
	 * @return {Array}  the children
	 */
	getChildren() {
		if (!this.children) {
			this.children = [];
		}
		return this.children;
	}

	/**
	 * @param {Array} children  the children to set
	 */
	setChildren(children) {
		this.children = children;
	}

	addChildren(child) {
		this.getChildren().push(child);
	}

	removeChildren(child) {
		var i = this.getChildren().indexOf(child);
		if (i >= 0) {
			this.getChildren().splice(i, 1);
		}
	}

	/**
	 * @return {MetricRoleType}  the metricRoleType
	 */
	getMetricRoleType() {
		return this.metricRoleType;
	}

	/**
	 * @param {MetricRoleType} metricRoleType  the metricRoleType to set
	 */
	setMetricRoleType(metricRoleType) {
		this.metricRoleType = metricRoleType;
	}

	/**
	 * @return {SentimentType}  the metricSentiment
	 */
	getMetricSentiment() {
		return this.metricSentiment;
	}

	/**
	 * @param {SentimentType} metricSentiment  the metricSentiment to set
	 */
	setMetricSentiment(metricSentiment) {
		this.metricSentiment = metricSentiment;
	}

	clone() {
		var cloned = new MetricDefinitionHierarchyItem(this.getRef());
		if (this.getRelatedRef()) {
			cloned.setRelatedRef(this.getRelatedRef());
		}
		if (this.getRelationshipRef()) {
			cloned.setRelationshipRef(this.getRelationshipRef());
		}
		if (this.getMetricRoleType()) {
			cloned.setMetricRoleType(this.getMetricRoleType());
		}
		if (this.getMetricSentiment()) {
			cloned.setMetricSentiment(this.getMetricSentiment());
		}
		var clonedChildren = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getChildren(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var clonedItem = item.clone();
			clonedChildren.push(clonedItem);
		}
		cloned.setChildren(clonedChildren);
		return cloned;
	}

	toJSON() {
		var json = {};
		if (this.getRef()) {
			json["ref"] = this.getRef().getIdForExpression();
		}
		if (this.getRelatedRef()) {
			json["relatedRef"] = this.getRelatedRef().getIdForExpression();
		}
		if (this.getRelationshipRef()) {
			json["relationshipRef"] = this.getRelationshipRef().getIdentifier();
		}
		if (this.getMetricRoleType()) {
			json["metricRoleType"] = this.getMetricRoleType().value();
		}
		if (this.getMetricSentiment()) {
			json["metricSentiment"] = this.getMetricSentiment().value();
		}
		if (this.children && !(this.children.length==0)) {
			var childArray = [];
			for (var __i_enFor0 = 0, __exp_enFor0 = this.getChildren(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var child = __exp_enFor0[__i_enFor0];
				childArray.push(child.toJSON());
			}
			json["children"] = childArray;
		}
		return json;
	}

	static fromJSON(obj, module) {
		var metricRoleTypeValue = obj["metricRoleType"];
		var metricSentimentValue = obj["metricSentiment"];
		var refIdForExpressionValue = obj["ref"];
		var relatedRefIdForExpressionValue = obj["relatedRef"];
		var relationshipRefIdForExpressionValue = obj["relationshipRef"];
		var childrenValue = obj["children"];
		var metricRoleType;
		var metricSentimentType;
		var ref;
		var relatedRef;
		var relationshipRef;
		var children;
		ref = ReferenceResolver.getMoserObjectByIdForExpression(refIdForExpressionValue, module);
		relatedRef = ReferenceResolver.getMoserObjectByIdForExpression(relatedRefIdForExpressionValue, module);
		if (metricRoleTypeValue != null) {
			metricRoleType = MetricRoleType.fromValue(metricRoleTypeValue);
		}
		if (metricSentimentValue != null) {
			metricSentimentType = SentimentType.fromValue(metricSentimentValue);
		}
		if (relationshipRefIdForExpressionValue != null) {
			relationshipRef = ReferenceResolver.getMoserObjectByIdForExpression(relationshipRefIdForExpressionValue, module);
		}
		if (childrenValue != null) {
			children = MetricDefinitionHierarchyItem.populateChildren(childrenValue, module);
		}
		var item = new MetricDefinitionHierarchyItem(ref);
		item.setRelatedRef(relatedRef);
		item.setMetricRoleType(metricRoleType);
		item.setRelationshipRef(relationshipRef);
		item.setMetricSentiment(metricSentimentType);
		item.setChildren(children);
		return item;
	}

	static populateChildren(objList, module) {
		var children;
		if (!objList) {
			return children;
		}
		children = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = objList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			children.push(MetricDefinitionHierarchyItem.fromJSON(obj, module));
		}
		return children;
	}
}


// WEBPACK FOOTER //
// ./src/generated/MetricDefinitionHierarchyItem.js